import React from "react";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import KeyboardVoiceOutlinedIcon from "@mui/icons-material/KeyboardVoiceOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import colors from "../assets/theme/colors";
import { landingPageStyles } from "../assets/theme/landingTheme";
import Footer from "../components/Landing/Footer";

const pageColors = {
  ink: "#17251f",
  muted: "#52645c",
  sage: colors.semantic.sageDeep,
  sageSoft: "#e8f4ef",
  lavender: colors.brand.deep,
  lavenderSoft: colors.brand.ice,
  border: "#dfe8e3",
};

const practiceSteps = [
  ["Recommend at intake", "Takes 2 minutes. Families set it up before they leave."],
  ["Families log their week", "Behaviors, sleep, meals, moods — in real time, in their own words."],
  ["You get a shared view", "With caregiver permission, see what happened between sessions."],
  ["Sessions start informed", "No guesswork. Real data, ready to go from session one."],
];

const parentSteps = [
  {
    label: "Parent",
    title: "Logs what happened",
    body: "Voice, text, or photo — captured in the moment, in under 10 seconds",
    dark: false,
  },
  {
    label: "Lifelog",
    title: "Organizes everything",
    body: "Builds a chronological timeline — no forms, no sorting required",
    dark: true,
  },
  {
    label: "Therapist",
    title: "Arrives prepared",
    body: "Sees the full week before the session — real context, no guesswork",
    dark: false,
  },
];

const parentFeatureCards = [
  {
    title: "Log in under 10 seconds",
    body: "Talk, type, or paste. Capture the moment right when it happens — no forms, no app to learn.",
    Icon: KeyboardVoiceOutlinedIcon,
  },
  {
    title: "Organized automatically",
    body: "A timeline of your child's week builds itself. No sorting, no extra work from you.",
    Icon: CalendarMonthOutlinedIcon,
  },
  {
    title: "Share with your therapist",
    body: "One tap gives your therapist a view of what happened — in your words, when it occurred.",
    Icon: ReplayOutlinedIcon,
  },
];

const phoneEntries = [
  ["Mon · 3:42 PM", "Tough meltdown after pickup. Loud music in the car seemed to trigger it.", "behavior"],
  ["Tue · 7:15 AM", "Refused breakfast again. Third morning in a row. Just crackers.", "food"],
  ["Wed · 9:30 PM", "Slept through the night. First time in 2 weeks. Way calmer all day.", "great day"],
  ["Thu · 4:00 PM", "Visual timer from last session worked perfectly on the transition.", "win"],
];

const therapistSummaryItems = [
  "Meltdown Mon after pickup — noise trigger identified",
  "Food refusal ongoing — 3 consecutive mornings",
  "First full night sleep in 2 weeks on Wed",
  "Visual timer strategy worked well Thu",
];

const therapistPatternItems = [
  "Sleep disruptions correlate with behavior peaks",
  "Morning transitions most difficult this week",
];

const therapistFeatureCards = [
  {
    title: "Context from day one",
    body: "Recommend at intake. Families set up in 2 minutes. By session two, you already have a week of real data.",
    Icon: PersonOutlineRoundedIcon,
  },
  {
    title: "See the full week",
    body: "Behaviors, sleep, meals, moods — in the family's own words, as it happened. Not what they remember later.",
    Icon: AssignmentOutlinedIcon,
  },
  {
    title: "Session summaries, one tap",
    body: "Generate a summary from the family's logs before you walk in. Real context, every session.",
    Icon: CalendarMonthOutlinedIcon,
  },
];

const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      color: pageColors.sage,
      fontSize: "0.82rem",
      fontWeight: 850,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
    }}
  >
    {children}
  </Typography>
);

const BackToLandingLink = () => (
  <Button
    href="/"
    startIcon={<ArrowBackRoundedIcon />}
    sx={{
      alignSelf: "flex-start",
      borderRadius: "999px",
      px: 1.4,
      py: 0.7,
      color: pageColors.sage,
      fontWeight: 850,
      textTransform: "none",
      "&:hover": { bgcolor: pageColors.sageSoft },
    }}
  >
    Back to home
  </Button>
);

const BulletList = ({ items }) => (
  <Box component="ul" sx={{ m: 0, pl: 2.2, color: pageColors.muted }}>
    {items.map((item) => (
      <Typography
        key={item}
        component="li"
        sx={{
          pl: 0.5,
          mb: 0.65,
          fontSize: { xs: "1rem", md: "1.08rem" },
          lineHeight: 1.45,
          "&::marker": {
            color: pageColors.sage,
          },
        }}
      >
        {item}
      </Typography>
    ))}
  </Box>
);

const AutismSupportPage = () => (
  <Box sx={landingPageStyles}>
    <Container maxWidth="lg" sx={{ px: { xs: 2.5, md: 4 }, py: { xs: 5, md: 7 } }}>
      <Stack spacing={{ xs: 5, md: 7 }}>
        <BackToLandingLink />

        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            px: { xs: 2.5, md: 6 },
            py: { xs: 6, md: 9 },
            borderRadius: { xs: 4, md: 6 },
            bgcolor: pageColors.lavenderSoft,
            border: `1px solid ${colors.landing.borderLight}`,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: { xs: 210, md: 320 },
              height: { xs: 210, md: 320 },
              borderRadius: "50%",
              right: { xs: -130, md: -90 },
              top: { xs: -100, md: -130 },
              bgcolor: "rgba(125, 104, 182, 0.08)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: { xs: 180, md: 270 },
              height: { xs: 180, md: 270 },
              borderRadius: "50%",
              left: { xs: -120, md: -85 },
              bottom: { xs: -110, md: -140 },
              bgcolor: "rgba(116, 170, 150, 0.12)",
            }}
          />
          <Stack spacing={2.5} alignItems="center" sx={{ position: "relative" }}>
            <Typography
              component="h1"
              sx={{
                maxWidth: 820,
                fontFamily: "'Fraunces', Georgia, serif",
                color: colors.landing.heroText,
                fontSize: { xs: "2.55rem", md: "4.7rem" },
                fontWeight: 760,
                lineHeight: 0.98,
              }}
            >
              Everything that happens between sessions, captured.
            </Typography>
            <Typography
              sx={{
                maxWidth: 680,
                color: colors.landing.bodyText,
                fontSize: { xs: "1.05rem", md: "1.25rem" },
                lineHeight: 1.6,
              }}
            >
              Lifelog connects what families live at home to what happens in therapy, school, and
              care conversations.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ pt: 1 }}>
              <Button
                component="a"
                href="#parent-support"
                startIcon={<PersonOutlineRoundedIcon />}
                variant="contained"
                sx={{
                  borderRadius: "999px",
                  px: 3,
                  py: 1.2,
                  textTransform: "none",
                  fontWeight: 900,
                  color: "#fff",
              bgcolor: pageColors.lavender,
                  boxShadow: "none",
              "&:hover": { bgcolor: pageColors.lavender, boxShadow: "none" },
                }}
                >
                Parents & caregivers
              </Button>
              <Button
                component="a"
                href="#therapist-support"
                startIcon={<AssignmentOutlinedIcon />}
                variant="outlined"
                sx={{
                  borderRadius: "999px",
                  px: 3,
                  py: 1.2,
                  textTransform: "none",
                  fontWeight: 900,
                  color: pageColors.sage,
                  bgcolor: "#fff",
                  borderColor: colors.landing.borderMedium,
                  "&:hover": {
                    bgcolor: "#fff",
                    borderColor: pageColors.sage,
                  },
                }}
              >
                I'm a therapist
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Box id="parent-support" sx={{ scrollMarginTop: 96 }}>
          <SectionLabel>For families</SectionLabel>
          <Typography
            component="h2"
            sx={{
              mt: 1.5,
              maxWidth: 820,
              fontFamily: "'Fraunces', Georgia, serif",
              color: pageColors.ink,
              fontSize: { xs: "2.45rem", md: "4rem" },
              fontWeight: 700,
              lineHeight: 0.98,
            }}
          >
            You're already tracking everything in your head.
          </Typography>
          <Typography
            sx={{
              mt: 2.2,
              maxWidth: 620,
              color: pageColors.muted,
              fontSize: { xs: "1.05rem", md: "1.22rem" },
              lineHeight: 1.55,
            }}
          >
            Lifelog makes it easy to capture it — so you stop relying on memory when it matters most.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1.2fr 0.8fr" },
            gap: { xs: 4, lg: 7 },
            alignItems: "center",
          }}
        >
          <Stack spacing={2.2}>
            {parentFeatureCards.map(({ title, body, Icon }) => (
              <Box
                key={title}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "52px 1fr",
                  gap: 2,
                  alignItems: "center",
                  p: { xs: 2.2, md: 3 },
                  border: `1px solid ${pageColors.border}`,
                  borderRadius: 3,
                  bgcolor: "#fff",
                  boxShadow: "0 20px 50px rgba(31, 45, 38, 0.06)",
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: pageColors.sageSoft,
                    color: pageColors.sage,
                  }}
                >
                  <Icon fontSize="small" />
                </Box>
                <Box>
                  <Typography sx={{ color: pageColors.ink, fontWeight: 900 }}>{title}</Typography>
                  <Typography sx={{ mt: 0.4, color: pageColors.muted, lineHeight: 1.5 }}>{body}</Typography>
                </Box>
              </Box>
            ))}
            <Button
              href="/login"
              endIcon={<ChevronRightRoundedIcon />}
              variant="contained"
              sx={{
                alignSelf: { xs: "stretch", sm: "flex-start" },
                borderRadius: "999px",
                px: 4,
                py: 1.25,
                textTransform: "none",
                fontWeight: 900,
                bgcolor: pageColors.lavender,
                boxShadow: "0 18px 38px rgba(124, 106, 174, 0.22)",
                "&:hover": {
                  bgcolor: pageColors.lavender,
                  boxShadow: "0 18px 38px rgba(124, 106, 174, 0.22)",
                },
              }}
            >
              Start your family timeline
            </Button>
          </Stack>

          <Box
            sx={{
              mx: "auto",
              width: { xs: 260, md: 300 },
              p: 2,
              borderRadius: 8,
              bgcolor: pageColors.ink,
              boxShadow: "0 28px 70px rgba(31, 45, 38, 0.18)",
            }}
          >
            <Box
              sx={{
                height: 26,
                width: 74,
                mx: "auto",
                mb: 2,
                borderRadius: 3,
                bgcolor: "#080d0b",
              }}
            />
            <Box sx={{ borderRadius: 5, bgcolor: "#fbfaf7", p: 2.2 }}>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.4 }}>
                <Typography sx={{ color: pageColors.ink, fontFamily: "'Fraunces', Georgia, serif", fontWeight: 800 }}>
                  This week
                </Typography>
                <Typography sx={{ color: pageColors.muted, fontSize: "0.72rem", fontWeight: 850 }}>June 2–8</Typography>
              </Stack>
              <Stack spacing={1.2}>
                {phoneEntries.map(([time, note, tag]) => (
                  <Box key={time} sx={{ p: 1.3, border: `1px solid ${pageColors.border}`, borderRadius: 2 }}>
                    <Typography
                      sx={{
                        color: pageColors.sage,
                        fontSize: "0.7rem",
                        fontWeight: 900,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      {time}
                    </Typography>
                    <Typography sx={{ mt: 0.4, color: pageColors.ink, fontSize: "0.78rem", lineHeight: 1.35 }}>
                      {note}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        display: "inline-block",
                        mt: 0.8,
                        px: 0.8,
                        py: 0.2,
                        borderRadius: 999,
                        bgcolor: pageColors.lavenderSoft,
                        color: pageColors.lavender,
                        fontSize: "0.62rem",
                        fontWeight: 900,
                      }}
                    >
                      {tag}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Box>
        </Box>

        <Box sx={{ py: { xs: 4, md: 6 }, px: { xs: 2.5, md: 5 }, borderRadius: 5, bgcolor: pageColors.sageSoft }}>
          <Stack spacing={1.2} alignItems="center" sx={{ textAlign: "center", mb: { xs: 4, md: 5 } }}>
            <Typography
              component="h2"
              sx={{
                fontFamily: "'Fraunces', Georgia, serif",
                color: pageColors.ink,
                fontSize: { xs: "2rem", md: "3rem" },
                fontWeight: 800,
                lineHeight: 1.05,
              }}
            >
              How home connects to therapy
            </Typography>
            <Typography sx={{ color: pageColors.muted, fontSize: { xs: "1rem", md: "1.15rem" } }}>
              Parents capture what happens. Lifelog organizes it. Therapists arrive prepared.
            </Typography>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: { xs: 2.5, md: 3.5 },
              alignItems: "stretch",
            }}
          >
            {parentSteps.map((step) => (
              <Box
                key={step.title}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  bgcolor: step.dark ? "#f1ecfb" : "#fff",
                  border: step.dark ? `1px solid ${colors.brand.tint}` : "1px solid transparent",
                  color: pageColors.ink,
                  textAlign: "center",
                  boxShadow: "0 20px 55px rgba(31, 45, 38, 0.07)",
                }}
              >
                <Stack spacing={1.3} alignItems="center">
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: step.dark ? "#e4d9f6" : pageColors.sageSoft,
                      color: step.dark ? pageColors.lavender : pageColors.sage,
                    }}
                  >
                    <AssignmentOutlinedIcon fontSize="small" />
                  </Box>
                  <Typography
                    sx={{
                      color: step.dark ? pageColors.lavender : pageColors.sage,
                      fontSize: "0.72rem",
                      fontWeight: 900,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                    }}
                  >
                    {step.label}
                  </Typography>
                  <Typography sx={{ color: "inherit", fontWeight: 900, fontSize: "1.08rem" }}>{step.title}</Typography>
                  <Typography
                    sx={{
                      color: pageColors.muted,
                      lineHeight: 1.55,
                      fontSize: "0.95rem",
                    }}
                  >
                    {step.body}
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          id="therapist-support"
          sx={{
            borderTop: `1px solid ${pageColors.border}`,
            pt: { xs: 5, md: 7 },
            scrollMarginTop: 96,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "0.95fr 1.05fr" },
            gap: { xs: 4, lg: 7 },
            alignItems: "center",
          }}
        >
          <Box>
            <SectionLabel>For ABA therapists</SectionLabel>
            <Typography
              component="h2"
              sx={{
                mt: 1.5,
                maxWidth: 780,
                fontFamily: "'Fraunces', Georgia, serif",
                color: pageColors.ink,
                fontSize: { xs: "2.4rem", md: "4.15rem" },
                fontWeight: 800,
                lineHeight: 1.02,
              }}
            >
              Know their week before they walk in.
            </Typography>
            <Typography
              sx={{
                mt: 2.4,
                maxWidth: 680,
                color: pageColors.muted,
                fontSize: { xs: "1.08rem", md: "1.35rem" },
                lineHeight: 1.55,
              }}
            >
              When families log at home, you get context you'd normally spend months building —
              starting from session one.
            </Typography>
          </Box>

          <Box
            sx={{
              position: "relative",
              p: { xs: 1.2, md: 1.8 },
              pb: { xs: 2.1, md: 2.6 },
              borderRadius: { xs: 5, md: 7 },
              bgcolor: "#141c19",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              boxShadow: "0 30px 70px rgba(20, 28, 25, 0.22)",
              "&::after": {
                content: '""',
                position: "absolute",
                left: "50%",
                bottom: { xs: 7, md: 9 },
                width: { xs: 34, md: 42 },
                height: 4,
                borderRadius: 999,
                bgcolor: "rgba(255, 255, 255, 0.42)",
                transform: "translateX(-50%)",
              },
            }}
          >
            <Stack spacing={1.2}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "rgba(255, 255, 255, 0.5)",
                  mx: "auto",
                  boxShadow: "0 0 0 3px rgba(255, 255, 255, 0.16)",
                }}
              />
              <Box
                sx={{
                  p: { xs: 2.5, md: 3 },
                  borderRadius: { xs: 4, md: 5 },
                  bgcolor: pageColors.lavenderSoft,
                  border: `1px solid ${colors.landing.borderLight}`,
                }}
              >
                <Stack spacing={2.4}>
                  <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={2}>
                    <Box>
                      <SectionLabel>Session summary</SectionLabel>
                      <Typography
                        sx={{
                          mt: 1,
                          color: pageColors.ink,
                          fontFamily: "'Fraunces', Georgia, serif",
                          fontSize: { xs: "1.75rem", md: "2.1rem" },
                          fontWeight: 800,
                        }}
                      >
                        Alex, age 7
                      </Typography>
                    </Box>
                    <Typography sx={{ color: pageColors.muted, fontWeight: 750 }}>June 4, 2026</Typography>
                  </Stack>

                  <Box sx={{ height: 1, bgcolor: colors.landing.borderLight }} />

                  <Stack spacing={1.1}>
                    <Typography
                      sx={{
                        color: pageColors.sage,
                        fontSize: "0.78rem",
                        fontWeight: 900,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                      }}
                    >
                      This week
                    </Typography>
                    <BulletList items={therapistSummaryItems} />
                  </Stack>

                  <Stack spacing={1.1}>
                    <Typography
                      sx={{
                        color: pageColors.sage,
                        fontSize: "0.78rem",
                        fontWeight: 900,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                      }}
                    >
                      Patterns to note
                    </Typography>
                    <BulletList items={therapistPatternItems} />
                  </Stack>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      p: 1.4,
                      borderRadius: 3,
                      bgcolor: pageColors.sage,
                      color: "#fff",
                      fontWeight: 900,
                    }}
                  >
                    <AssignmentOutlinedIcon fontSize="small" />
                    Generated from family logs
                  </Box>
                  <Typography sx={{ textAlign: "center", color: pageColors.muted, fontSize: "0.85rem" }}>
                    1 tap · ready before your session
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: { xs: 2.5, md: 3 },
          }}
        >
          {therapistFeatureCards.map(({ title, body, Icon }) => (
            <Box
              key={title}
              sx={{
                p: { xs: 3, md: 4 },
                border: `1px solid ${pageColors.border}`,
                borderRadius: 4,
                bgcolor: "#fff",
                boxShadow: "0 22px 55px rgba(31, 45, 38, 0.05)",
              }}
            >
              <Stack spacing={2}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: pageColors.sageSoft,
                    color: pageColors.sage,
                  }}
                >
                  <Icon />
                </Box>
                <Typography sx={{ color: pageColors.ink, fontWeight: 900, fontSize: "1.25rem", lineHeight: 1.2 }}>
                  {title}
                </Typography>
                <Typography sx={{ color: pageColors.muted, fontSize: "1.05rem", lineHeight: 1.6 }}>{body}</Typography>
              </Stack>
            </Box>
          ))}
        </Box>

        <Box>
          <SectionLabel>How it works with your practice</SectionLabel>
          <Box
            sx={{
              mt: 3,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
              gap: { xs: 2.5, md: 3 },
            }}
          >
            {practiceSteps.map(([title, body], index) => (
              <Stack key={title} spacing={1.5}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: pageColors.sageSoft,
                    color: pageColors.sage,
                    fontWeight: 900,
                  }}
                >
                  {index + 1}
                </Box>
                <Typography sx={{ color: pageColors.ink, fontWeight: 900, fontSize: "1.12rem" }}>
                  {title}
                </Typography>
                <Typography sx={{ color: pageColors.muted, lineHeight: 1.55 }}>{body}</Typography>
              </Stack>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "stretch", sm: "flex-start" },
          }}
        >
          <Button
            href="/login"
            endIcon={<ChevronRightRoundedIcon />}
            variant="contained"
            sx={{
              width: { xs: "100%", sm: "auto" },
              borderRadius: "999px",
              px: 4,
              py: 1.4,
              textTransform: "none",
              fontWeight: 900,
              bgcolor: pageColors.sage,
              boxShadow: "0 18px 38px rgba(111, 165, 148, 0.22)",
              "&:hover": {
                bgcolor: pageColors.sage,
                boxShadow: "0 18px 38px rgba(111, 165, 148, 0.22)",
              },
            }}
          >
            Recommend Lifelog to your families
          </Button>
        </Box>

      </Stack>
    </Container>

    <Footer variant="desktop" />
  </Box>
);

export default AutismSupportPage;
